// ds-typo-ignore-file — mockup simulates report viewer UI, not marketing site DS
"use client"
import { blobMediaUrl } from "@/lib/blob-url"

/**
 * Pixel-exact recreation of the report dashboard summary cards.
 * Values extracted from my.aheadhealth.com DOM, scaled to ~87% for iPad mockup.
 */

import { m } from "framer-motion"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { createMountFadeUp } from "@/lib/motion"

const COLORS = {
  deepGreen: "#408c49",
  lightGreen: "#b8c664",
  terracotta: "#ed9062",
}

const anim = (delay: number) => ({
  ...createMountFadeUp(delay),
  whileHover: {
    scale: 1.02,
    boxShadow: "0 8px 30px rgba(45, 45, 45, 0.12)",
    transition: { duration: 0.15, ease: [0.16, 1, 0.3, 1] },
  } as const,
})

/* -- Bio Age Card ------------------------------------------------ */

function BioAgeCard() {
  const t = useTranslations("report")
  return (
    <m.div
      {...anim(0.7)}
      data-explainer="bioAge"
      className="relative flex h-[144px] flex-col overflow-hidden rounded-[14px] bg-cover bg-center px-[14px] pt-[10px] pb-[18px] text-white"
      style={{
        padding: "10px 14px 18px",
      }}
    >
      <Image
        src={blobMediaUrl("/images/report/backgrounds/summary_bg_1.png")}
        alt=""
        fill
        className="z-0 object-cover object-center"
      />
      {/* Info icon top-right */}
      <div className="absolute top-[10px] right-[14px] z-10 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-500/25 text-[14px] text-white">
        +
      </div>

      <span className="relative z-10 text-[28px] leading-none font-medium text-white">
        38
      </span>
      <span className="relative z-10 mt-0.5 text-[14px] leading-none font-normal text-white">
        {t("mockupBioAge")}
      </span>

      {/* Progress bar area */}
      <div className="relative z-10 mt-auto w-full">
        {/* Track */}
        <div className="relative h-[6px] w-full rounded-full bg-white/20">
          {/* Center tick */}
          <div className="absolute top-1/2 left-1/2 h-[8px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/75" />
          {/* Hatched fill from center toward left */}
          <div
            className="absolute top-0 h-[6px] rounded-full"
            style={{
              right: "50%",
              width: "15%",
              background:
                "repeating-linear-gradient(-45deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 1.5px, rgba(255,255,255,0.3) 1.5px, rgba(255,255,255,0.3) 3px)",
            }}
          />
        </div>
        {/* White pill with down-arrow — matches bioAge card on /report */}
        <div
          className="absolute top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 shadow-sm"
          style={{ left: "-2px" }}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-neutral-900"
          >
            <path d="M5 1v8M2 6l3 3 3-3" />
          </svg>
          <span className="text-[10px] leading-none font-medium text-neutral-900">
            {t("mockupBioAgeYearsShort")}
          </span>
        </div>
      </div>

      {/* Description */}
      <span className="relative z-10 mt-1.5 text-[10px] leading-tight text-white/70">
        {t("mockupBioAgeDesc")}
      </span>
    </m.div>
  )
}

/* -- Heart Risk Card --------------------------------------------- */

function HeartRiskCard() {
  const t = useTranslations("report")
  return (
    <m.div
      {...anim(0.75)}
      data-explainer="heartRisk"
      className="relative flex h-[144px] flex-col overflow-hidden rounded-[14px] bg-cover bg-center px-[14px] pt-[10px] pb-[18px] text-white"
      style={{
        padding: "10px 14px 18px",
      }}
    >
      <Image
        src={blobMediaUrl("/images/report/backgrounds/summary_bg_2.png")}
        alt=""
        fill
        className="z-0 object-cover object-center"
      />
      {/* Info icon top-right */}
      <div className="absolute top-[10px] right-[14px] z-10 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-500/25 text-[14px] text-white">
        +
      </div>

      <span className="relative z-10 text-[28px] leading-none font-medium text-white">
        1.6%
      </span>
      <span className="relative z-10 mt-0.5 text-[14px] leading-none font-normal text-white">
        {t("mockupHeartRisk")}
      </span>

      {/* Progress bar */}
      <div className="relative z-10 mt-auto w-full">
        <div className="h-[6px] w-full rounded-full bg-white/20">
          <div className="h-full w-[16%] rounded-full bg-white/60" />
        </div>
      </div>

      {/* Description */}
      <span className="relative z-10 mt-1.5 text-[10px] leading-tight text-white/70">
        {t("mockupHeartRiskDesc")}
      </span>
    </m.div>
  )
}

/* -- Biomarkers Card --------------------------------------------- */

function BiomarkersCard() {
  const t = useTranslations("report")
  return (
    <m.div
      {...anim(0.85)}
      data-explainer="biomarkers"
      className="relative flex h-[144px] flex-col overflow-hidden rounded-[14px] bg-white px-[14px] pt-[10px] pb-[18px] shadow-[0_0_30px_rgba(45,45,45,0.04)]"
    >
      <span className="text-foreground text-[28px] leading-none font-medium">
        89
      </span>
      <span className="text-foreground mt-0.5 text-[14px] font-normal">
        {t("mockupBiomarkers")}
      </span>

      {/* Range bar */}
      <div className="mt-auto flex h-[6px] w-full gap-[1px] overflow-hidden rounded-full">
        <div
          className="rounded-full"
          style={{ flex: 12, backgroundColor: COLORS.deepGreen }}
        />
        <div
          className="rounded-full"
          style={{ flex: 73, backgroundColor: COLORS.lightGreen }}
        />
        <div
          className="rounded-full"
          style={{ flex: 4, backgroundColor: COLORS.terracotta }}
        />
      </div>

      {/* Badges */}
      <div className="mt-2 flex gap-1">
        <span className="text-foreground inline-flex items-center gap-[3px] rounded-[40px] border border-neutral-100 px-[6px] py-[1.5px] text-[10px]">
          <span className="font-medium">12</span>
          <span className="font-normal">{t("mockupOptimal")}</span>
        </span>
        <span className="text-foreground inline-flex items-center gap-[3px] rounded-[40px] border border-neutral-100 px-[6px] py-[1.5px] text-[10px]">
          <span className="font-medium">73</span>
          <span className="font-normal">{t("mockupInRange")}</span>
        </span>
        <span className="text-foreground inline-flex items-center gap-[3px] rounded-[40px] border border-neutral-100 px-[6px] py-[1.5px] text-[10px]">
          <span className="font-medium">4</span>
          <span className="font-normal">{t("mockupOutOfRange")}</span>
        </span>
      </div>
    </m.div>
  )
}

/* -- Findings Card ----------------------------------------------- */

function FindingsCard() {
  const t = useTranslations("report")
  return (
    <m.div
      {...anim(0.9)}
      data-explainer="findings"
      className="relative flex h-[144px] flex-col overflow-hidden rounded-[14px] bg-white px-[14px] pt-[10px] pb-[18px] shadow-[0_0_30px_rgba(45,45,45,0.04)]"
    >
      <span className="text-foreground text-[28px] leading-none font-medium">
        10
      </span>
      <span className="text-foreground mt-0.5 text-[14px] font-normal">
        {t("mockupFindings")}
      </span>

      {/* Badges with colored dots */}
      <div className="mt-auto flex flex-wrap gap-1">
        <span className="text-foreground inline-flex items-center gap-[3px] rounded-[40px] border border-neutral-100 px-[6px] py-[1.5px] text-[10px]">
          <span className="h-1 w-1 rounded-full bg-neutral-200" />
          <span className="font-medium">1</span>
          <span className="font-normal">{t("mockupNoFinding")}</span>
        </span>
        <span className="text-foreground inline-flex items-center gap-[3px] rounded-[40px] border border-neutral-100 px-[6px] py-[1.5px] text-[10px]">
          <span
            className="h-1 w-1 rounded-full"
            style={{ backgroundColor: COLORS.deepGreen }}
          />
          <span className="font-medium">5</span>
          <span className="font-normal">{t("mockupObservation")}</span>
        </span>
        <span className="text-foreground inline-flex items-center gap-[3px] rounded-[40px] border border-neutral-100 px-[6px] py-[1.5px] text-[10px]">
          <span
            className="h-1 w-1 rounded-full"
            style={{ backgroundColor: COLORS.lightGreen }}
          />
          <span className="font-medium">4</span>
          <span className="font-normal">{t("mockupMinor")}</span>
        </span>
      </div>
    </m.div>
  )
}

/* -- Summary Card (full width) ----------------------------------- */

function SummaryCard() {
  const t = useTranslations("report")
  return (
    <m.div
      {...anim(1.0)}
      data-explainer="summary"
      className="col-span-full flex flex-col overflow-hidden rounded-[14px] bg-white px-[14px] pt-[10px] pb-[18px] shadow-[0_0_30px_rgba(45,45,45,0.04)]"
    >
      <span className="text-foreground text-[16px] font-normal">
        {t("mockupSummary")}
      </span>

      {/* Doctor row */}
      <div className="mt-1 flex items-center gap-1.5">
        <Image
          src={blobMediaUrl("/images/about/olivio-donati.jpg")}
          alt="Prof. Dr. med. Olivio Donati"
          width={24}
          height={24}
          className="h-6 w-6 rounded-full object-cover"
        />
        <span className="text-foreground/70 text-[12px]">
          Prof. Dr. med. Olivio Donati
        </span>
      </div>

      {/* Message */}
      <span className="text-foreground/60 mt-1.5 text-[12px]">
        {t("mockupHello")}
      </span>
      <p className="text-foreground/60 mt-0.5 line-clamp-2 text-[11px] leading-relaxed">
        {t("mockupSummaryText")}
      </p>

      {/* Read more */}
      <div className="text-foreground/50 mt-1 flex items-center gap-0.5 text-[11px]">
        {t("mockupReadMore")}
        <svg
          width="9"
          height="9"
          viewBox="0 0 8 8"
          fill="none"
          className="text-foreground/40"
        >
          <path
            d="M2 3L4 5L6 3"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Clinical follow-ups card */}
      <div className="bg-background mt-1.5 flex items-center gap-2.5 rounded-lg p-2">
        <Image
          src={blobMediaUrl("/images/report/followup-consultation.jpg")}
          alt=""
          width={40}
          height={40}
          className="h-10 w-10 shrink-0 rounded-md object-cover"
        />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-foreground text-[11px] font-medium">
            {t("mockupClinicalFollowUp")}
          </span>
          <span className="text-foreground/50 text-[10px]">
            {t("mockupSpecialistConsult")}
          </span>
        </div>
        {/* Chevron right */}
        <svg
          width="10"
          height="10"
          viewBox="0 0 8 8"
          fill="none"
          className="text-foreground/30 ml-auto shrink-0"
        >
          <path
            d="M3 2L5 4L3 6"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Health optimizations card */}
      <div className="bg-background mt-1.5 flex items-center gap-2.5 rounded-lg p-2">
        <Image
          src={blobMediaUrl("/images/report/followup-optimization.jpg")}
          alt=""
          width={40}
          height={40}
          className="h-10 w-10 shrink-0 rounded-md object-cover"
        />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-foreground text-[11px] font-medium">
            {t("mockupHealthOptimizations")}
          </span>
          <span className="text-foreground/50 text-[10px]">
            {t("mockupExerciseNutrition")}
          </span>
        </div>
        {/* Chevron right */}
        <svg
          width="10"
          height="10"
          viewBox="0 0 8 8"
          fill="none"
          className="text-foreground/30 ml-auto shrink-0"
        >
          <path
            d="M3 2L5 4L3 6"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </m.div>
  )
}

/* -- Grid -------------------------------------------------------- */

export function ReportMockupCards() {
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {/* Row 1: bio age + heart risk — stacks on narrow */}
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <BioAgeCard />
        <HeartRiskCard />
      </div>
      {/* Row 2: biomarkers + findings — stacks on narrow */}
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <BiomarkersCard />
        <FindingsCard />
      </div>
      {/* Row 3: summary full width */}
      <SummaryCard />
    </div>
  )
}
